import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import {
  ReactFlow, Background, Controls, MiniMap,
  addEdge, applyNodeChanges, applyEdgeChanges,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Save, Trash2, Rocket, Network } from "lucide-react";

/** Iter 170 · Mind Maps — visual brainstorming canvas (React Flow).
 * Nodes persist as {id,label,x,y,color,idea_id}; promote any node to a
 * tracked idea. */

const toRF = (n) => ({
  id: n.id, position: { x: n.x, y: n.y }, data: { label: n.label },
  style: { border: `2px solid ${n.color || "#f97316"}`, borderRadius: 6,
            fontSize: 12, padding: 6,
            background: n.idea_id ? "#ecfdf5" : "#fff" },
});
const fromRF = (n, orig) => ({
  id: n.id, label: n.data?.label || "", x: n.position.x, y: n.position.y,
  color: orig?.color || "#f97316", idea_id: orig?.idea_id || null,
});

export default function MindMapTab({ wsId }) {
  const [maps, setMaps] = useState([]);
  const [active, setActive] = useState(null); // full map doc
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [origById, setOrigById] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [dirty, setDirty] = useState(false);

  const loadList = useCallback(() => {
    api.get(`/workspaces/${wsId}/bizdev/mindmaps`)
      .then((r) => setMaps(r.data.rows || []))
      .catch((e) => toast.error(apiErrorMessage(e, "Failed to load maps")));
  }, [wsId]);
  useEffect(() => { loadList(); }, [loadList]);

  const openMap = async (mid) => {
    try {
      const { data } = await api.get(`/workspaces/${wsId}/bizdev/mindmaps/${mid}`);
      setActive(data);
      setNodes((data.nodes || []).map(toRF));
      setEdges((data.edges || []).map((e) => ({ ...e, animated: false })));
      setOrigById(Object.fromEntries((data.nodes || []).map((n) => [n.id, n])));
      setSelectedId(null);
      setDirty(false);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to open map")); }
  };

  const createMap = async () => {
    const title = newTitle.trim() || "Untitled map";
    try {
      const { data } = await api.post(`/workspaces/${wsId}/bizdev/mindmaps`, { title });
      setNewTitle("");
      loadList();
      openMap(data.id);
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
  };

  const saveMap = async () => {
    if (!active) return null;
    const payload = {
      title: active.title,
      nodes: nodes.map((n) => fromRF(n, origById[n.id])),
      edges: edges.map((e) => ({ id: e.id, source: e.source, target: e.target })),
    };
    try {
      const { data } = await api.put(
        `/workspaces/${wsId}/bizdev/mindmaps/${active.id}`, payload);
      setOrigById(Object.fromEntries((data.nodes || []).map((n) => [n.id, n])));
      setDirty(false);
      toast.success("Mind map saved");
      loadList();
      return data;
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); return null; }
  };

  const addNode = () => {
    const id = Math.random().toString(36).slice(2, 10);
    const anchor = nodes.find((n) => n.id === selectedId) || nodes[0];
    const pos = anchor
      ? { x: anchor.position.x + 180, y: anchor.position.y + (Math.random() * 120 - 60) }
      : { x: 0, y: 0 };
    setNodes((ns) => [
      ...ns.map((n) => ({ ...n, selected: false })),
      { ...toRF({ id, label: "New thought", x: pos.x, y: pos.y }), selected: true },
    ]);
    if (anchor) {
      setEdges((es) => [...es, { id: `e-${anchor.id}-${id}`, source: anchor.id, target: id }]);
    }
    setSelectedId(id);
    setDirty(true);
  };

  const deleteSelected = () => {
    if (!selectedId) return;
    setNodes((ns) => ns.filter((n) => n.id !== selectedId));
    setEdges((es) => es.filter((e) => e.source !== selectedId && e.target !== selectedId));
    setSelectedId(null);
    setDirty(true);
  };

  const promote = async () => {
    if (!selectedId) return;
    const saved = await saveMap();
    if (!saved) return;
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/bizdev/mindmaps/${active.id}/promote-node`,
        { node_id: selectedId });
      toast.success(`Promoted to idea: ${data.idea.title}`);
      openMap(active.id);
    } catch (e) { toast.error(apiErrorMessage(e, "Promote failed")); }
  };

  const removeMap = async (mid) => {
    if (!window.confirm("Delete this mind map?")) return;
    try {
      await api.delete(`/workspaces/${wsId}/bizdev/mindmaps/${mid}`);
      if (active?.id === mid) setActive(null);
      loadList();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const selected = nodes.find((n) => n.id === selectedId);
  const selectedOrig = origById[selectedId];

  return (
    <div className="grid lg:grid-cols-4 gap-3" data-testid="bizdev-mindmap-tab">
      <div className="space-y-2">
        <div className="flex gap-1.5">
          <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") createMap(); }}
                 placeholder="New map title…" className="h-8 text-xs"
                 data-testid="mindmap-new-title" />
          <Button size="sm" className="h-8 text-xs" onClick={createMap}
                  data-testid="mindmap-create-btn">
            <Plus className="w-3 h-3" />
          </Button>
        </div>
        <div className="border border-border divide-y" data-testid="mindmap-list">
          {maps.map((m) => (
            <div key={m.id}
                 className={`px-2.5 py-2 text-xs flex items-center gap-2 cursor-pointer ${active?.id === m.id ? "bg-primary/10 border-l-2 border-primary" : "hover:bg-muted/40"}`}
                 onClick={() => openMap(m.id)}
                 data-testid={`mindmap-item-${m.id}`}>
              <Network className="w-3 h-3 text-muted-foreground flex-shrink-0" />
              <span className="truncate flex-1 font-medium">{m.title}</span>
              <span className="text-[10px] text-muted-foreground">{m.node_count}</span>
              <button type="button"
                      onClick={(e) => { e.stopPropagation(); removeMap(m.id); }}
                      className="text-red-400 hover:text-red-600"
                      data-testid={`mindmap-delete-${m.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
          {maps.length === 0 && (
            <div className="p-6 text-center text-xs text-muted-foreground">
              No maps yet — name one above and hit +.
            </div>
          )}
        </div>
      </div>

      <div className="lg:col-span-3 space-y-2">
        {!active ? (
          <div className="border border-dashed border-border h-[520px] flex flex-col items-center justify-center text-sm text-muted-foreground">
            <Network className="w-8 h-8 mb-2" />
            Pick or create a mind map to start brainstorming.
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <Input value={active.title}
                     onChange={(e) => { setActive({ ...active, title: e.target.value }); setDirty(true); }}
                     className="h-8 text-xs w-52 font-medium" data-testid="mindmap-title-input" />
              <Button size="sm" variant="outline" className="h-8 text-xs"
                      onClick={addNode} data-testid="mindmap-add-node">
                <Plus className="w-3 h-3 mr-1" /> Node
              </Button>
              {selected && (
                <>
                  <Input value={selected.data?.label || ""}
                         onChange={(e) => {
                           setNodes((ns) => ns.map((n) => n.id === selectedId
                             ? { ...n, data: { ...n.data, label: e.target.value } } : n));
                           setDirty(true);
                         }}
                         className="h-8 text-xs w-44" placeholder="Node label"
                         data-testid="mindmap-node-label" />
                  <Button size="sm" variant="outline" className="h-8 text-xs"
                          onClick={deleteSelected} data-testid="mindmap-delete-node">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                  {!selectedOrig?.idea_id && (
                    <Button size="sm" variant="outline" className="h-8 text-xs text-emerald-700"
                            onClick={promote} data-testid="mindmap-promote-node">
                      <Rocket className="w-3 h-3 mr-1" /> Promote to idea
                    </Button>
                  )}
                </>
              )}
              <Button size="sm" className="h-8 text-xs ml-auto" onClick={saveMap}
                      disabled={!dirty} data-testid="mindmap-save-btn">
                <Save className="w-3 h-3 mr-1" /> {dirty ? "Save map" : "Saved"}
              </Button>
            </div>
            <div className="border border-border h-[500px]" data-testid="mindmap-canvas">
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={(ch) => { setNodes((ns) => applyNodeChanges(ch, ns)); setDirty(true); }}
                onEdgesChange={(ch) => { setEdges((es) => applyEdgeChanges(ch, es)); setDirty(true); }}
                onConnect={(params) => { setEdges((es) => addEdge(params, es)); setDirty(true); }}
                onSelectionChange={({ nodes: sel }) => setSelectedId(sel?.[0]?.id || null)}
                fitView
                proOptions={{ hideAttribution: true }}
              >
                <Background gap={18} size={1} />
                <Controls showInteractive={false} />
                <MiniMap pannable zoomable style={{ height: 80, width: 120 }} />
              </ReactFlow>
            </div>
            <div className="text-[10px] text-muted-foreground">
              Drag nodes · drag from a node edge to connect · green nodes are already tracked ideas.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
